import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap"
import { FaPlus, FaTimes, FaCheck, FaCalendarAlt, FaTicketAlt } from "react-icons/fa"
import axios from "axios"
import { localserver } from "../../../api/common"
import { showToast } from "../../Shared/ToastMessage"

const CreateCheckin = ({ show, onHide, onSubmit, checkInToEdit }) => {
  const [eventId, setEventId] = useState()
  const [checkInData, setCheckInData] = useState({
    id: null,
    user_id: "",
    checkin_name: "",
    event_id: null,
    ticket_ids: "",
    entry_type: "single",
    entry_limit: 1,
    checkin_start_date: "",
    checkin_end_date: "",
    checkin_success_msg: "",
    checkin_fail_msg: "",
    limit_exceeded_msg: "",
  })
  const [errors, setErrors] = useState({})
  const [ticketList, setTicketList] = useState([])
  const [isVisible, setIsVisible] = useState(false)
  const [isLoadingTickets, setIsLoadingTickets] = useState(false)
  const [ticketError, setTicketError] = useState(null)
  const [showTicketModal, setShowTicketModal] = useState(false)

  // Helper to convert UTC ISO string to local "YYYY-MM-DDTHH:mm" for datetime-local input
  const utcToLocalString = (utcIsoString) => {
    if (!utcIsoString) return ""
    const utcDate = new Date(utcIsoString)
    const offset = utcDate.getTimezoneOffset()
    const localDate = new Date(utcDate.getTime() - offset * 60000)
    return localDate.toISOString().slice(0, 16)
  }

  // Helper to convert local "YYYY-MM-DDTHH:mm" string to UTC "YYYY-MM-DD HH:mm:ss" for API
  const localStringToUtcIso = (localString) => {
    if (!localString) return null
    const [datePart, timePart] = localString.split("T")
    const [year, month, day] = datePart.split("-").map(Number)
    const [hours, minutes] = timePart.split(":").map(Number)
    const localDate = new Date(year, month - 1, day, hours, minutes)
    const offset = localDate.getTimezoneOffset()
    const utcDate = new Date(localDate.getTime() + offset * 60000)
    // Format as YYYY-MM-DD HH:mm:ss
    const yearStr = utcDate.getUTCFullYear()
    const monthStr = String(utcDate.getUTCMonth() + 1).padStart(2, "0")
    const dayStr = String(utcDate.getUTCDate()).padStart(2, "0")
    const hoursStr = String(utcDate.getUTCHours()).padStart(2, "0")
    const minutesStr = String(utcDate.getUTCMinutes()).padStart(2, "0")
    const secondsStr = String(utcDate.getUTCSeconds()).padStart(2, "0")
    return `${yearStr}-${monthStr}-${dayStr} ${hoursStr}:${minutesStr}:${secondsStr}`
  }

  // Initialize eventId from localStorage
  useEffect(() => {
    const eventId = localStorage.getItem("eventId")
    setEventId(eventId)
  }, [])

  // Initialize or update checkInData and ticketList when checkInToEdit changes
  useEffect(() => {
    if (checkInToEdit) {
      const updatedCheckInData = {
        id: checkInToEdit.id || null,
        user_id: checkInToEdit.user_id || "",
        checkin_name: checkInToEdit.checkin_name || "",
        event_id: checkInToEdit.event_id || null,
        ticket_ids: checkInToEdit.ticket_ids || "",
        entry_type: checkInToEdit.entry_type || "single",
        entry_limit: checkInToEdit.entry_limit || 1,
        checkin_start_date: utcToLocalString(checkInToEdit.checkin_start_date),
        checkin_end_date: utcToLocalString(checkInToEdit.checkin_end_date),
        checkin_success_msg: checkInToEdit.checkin_success_msg || "",
        checkin_fail_msg: checkInToEdit.checkin_fail_msg || "",
        limit_exceeded_msg: checkInToEdit.limit_exceeded_msg || "",
      }
      setCheckInData(updatedCheckInData)

      const ticketIds = checkInToEdit.ticket_ids ? checkInToEdit.ticket_ids.split(",").map(Number) : []
      setTicketList((prevTickets) =>
        prevTickets.length > 0
          ? prevTickets.map((ticket) => ({
            ...ticket,
            selected: ticketIds.includes(ticket.id),
          }))
          : [],
      )
    } else {
      setCheckInData({
        id: null,
        user_id: "",
        checkin_name: "",
        event_id: null,
        ticket_ids: "",
        entry_type: "single",
        entry_limit: 1,
        checkin_start_date: "",
        checkin_end_date: "",
        checkin_success_msg: "",
        checkin_fail_msg: "",
        limit_exceeded_msg: "",
      })
      setTicketList([])
    }
    setErrors({})
  }, [checkInToEdit])

  // Fetch tickets when eventId or checkInToEdit changes
  useEffect(() => {
    getTicketsByEventID()
  }, [eventId, checkInToEdit])

  useEffect(() => {
    if (show) {
      setIsVisible(true)
      document.body.style.overflow = "hidden"
    } else {
      setTimeout(() => {
        setIsVisible(false)
        document.body.style.overflow = ""
      }, 300)
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [show])

  const validateForm = () => {
    const newErrors = {}

    if (!checkInData.checkin_name.trim()) {
      newErrors.checkin_name = "Check-in point name is required."
    }

    const selectedTickets = ticketList.filter(t => t.selected).length
    if (selectedTickets === 0) {
      newErrors.ticket_ids = "At least one ticket must be selected."
    }

    if (!checkInData.checkin_start_date) {
      newErrors.checkin_start_date = "Start date and time is required."
    }

    if (!checkInData.checkin_end_date) {
      newErrors.checkin_end_date = "End date and time is required."
    }

    if (checkInData.checkin_start_date && checkInData.checkin_end_date) {
      const startDate = new Date(localStringToUtcIso(checkInData.checkin_start_date))
      const endDate = new Date(localStringToUtcIso(checkInData.checkin_end_date))
      if (endDate <= startDate) {
        newErrors.checkin_end_date = "End date and time must be after start date and time."
      }
    }

    if (checkInData.entry_type === "multiple" && (!checkInData.entry_limit || checkInData.entry_limit < 1)) {
      newErrors.entry_limit = "Entry limit must be at least 1."
    }

    if (!checkInData.checkin_success_msg) {
      newErrors.checkin_success_msg = "Successful check-in message is required."
    }

    if (!checkInData.checkin_fail_msg) {
      newErrors.checkin_fail_msg = "Failed check-in message is required."
    }

    if (!checkInData.limit_exceeded_msg) {
      newErrors.limit_exceeded_msg = "Check-in limit exceeded message is required."
    }


    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const getTicketsByEventID = async () => {
    setIsLoadingTickets(true)
    setTicketError(null)
    const storedEventId = localStorage.getItem("eventId") || eventId
    if (!storedEventId) {
      setTicketError("No event ID found. Please select an event.")
      setIsLoadingTickets(false)
      showToast("No event ID found. Please select an event.", "error")
      return
    }

    try {
      const response = await axios.post(
        `${localserver}/get_ticket_by_event_id`,
        {
          event_id: storedEventId,
          ticket_status: "all",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )
      if (response.data.success && Array.isArray(response.data.tickets)) {
        const ticketIds = checkInToEdit?.ticket_ids ? checkInToEdit.ticket_ids.split(",").map(Number) : []
        const updatedTickets = response.data.tickets.map((ticket) => ({
          id: ticket.id,
          ticket_name: ticket.ticket_name || ticket.name || "Unnamed Ticket",
          selected: ticketIds.includes(ticket.id),
        }))
        setTicketList(updatedTickets)
      } else {
        setTicketError("No tickets found for this event.")
        showToast("No tickets found for this event.", "error")
      }
    } catch (error) {
      console.error("Error fetching tickets:", error)
      setTicketError("Failed to fetch tickets. Please try again.")
      showToast("Failed to fetch tickets. Please try again.", "error")
    } finally {
      setIsLoadingTickets(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setCheckInData({
      ...checkInData,
      [name]: value,
    })
    // Clear error on input change
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      })
    }
  }

  const handleTicketSelect = (id) => {
    setTicketList((prevTickets) =>
      prevTickets.map((ticket) => (ticket.id === id ? { ...ticket, selected: !ticket.selected } : ticket)),
    )
    const selectedIds = ticketList
      .map((t) => (t.id === id ? { ...t, selected: !t.selected } : t))
      .filter((t) => t.selected)
      .map((t) => t.id)
      .join(",")
    setCheckInData({
      ...checkInData,
      ticket_ids: selectedIds,
    })
    // Clear ticket error on selection
    if (errors.ticket_ids) {
      setErrors({
        ...errors,
        ticket_ids: ""
      })
    }
  }

  const handleEntryTypeChange = (type) => {
    setCheckInData({
      ...checkInData,
      entry_type: type,
      entry_limit: type === "single" ? 1 : checkInData.entry_limit,
    })
    // Clear entry limit error if switching
    if (type === "single" && errors.entry_limit) {
      setErrors({
        ...errors,
        entry_limit: ""
      })
    }
  }

  const handleSelectAllTickets = () => {
    const allSelected = ticketList.every((ticket) => ticket.selected)
    setTicketList(
      ticketList.map((ticket) => ({
        ...ticket,
        selected: !allSelected,
      })),
    )
    setCheckInData({
      ...checkInData,
      ticket_ids: !allSelected ? ticketList.map((t) => t.id).join(",") : "",
    })
    // Clear ticket error on select all
    if (errors.ticket_ids) {
      setErrors({
        ...errors,
        ticket_ids: ""
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      showToast("Please fill all required fields", "error")
      return
    }

    const apiData = {
      user_id: checkInData.user_id || localStorage.getItem("loginUserId"),
      checkin_name: checkInData.checkin_name,
      event_id: eventId || localStorage.getItem("eventId") || "",
      ticket_ids: checkInData.ticket_ids,
      entry_type: checkInData.entry_type,
      entry_limit: checkInData.entry_limit,
      checkin_start_date: localStringToUtcIso(checkInData.checkin_start_date),
      checkin_end_date: localStringToUtcIso(checkInData.checkin_end_date),
      checkin_success_msg: checkInData.checkin_success_msg,
      checkin_fail_msg: checkInData.checkin_fail_msg,
      limit_exceeded_msg: checkInData.limit_exceeded_msg,
    }
    console.log(apiData, "apiData")
    try {
      const response = checkInData.id
        ? await axios.post(
          `${localserver}/checkin_master_add_update`,
          {
            ...apiData,
            id: checkInData.id,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          },
        )
        : await axios.post(`${localserver}/checkin_master_add_update`, apiData, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })

      if (response.data.success) {
        showToast(
          checkInData.id ? "Check-in point updated successfully!" : "Check-in point created successfully!",
          "success",
        )
        onSubmit(checkInData)
        setCheckInData({
          id: null,
          user_id: "",
          checkin_name: "",
          event_id: null,
          ticket_ids: "",
          entry_type: "single",
          entry_limit: 1,
          checkin_start_date: "",
          checkin_end_date: "",
          checkin_success_msg: "",
          checkin_fail_msg: "",
          limit_exceeded_msg: "",
        })
        setTicketList([])
        setErrors({})
        onHide()
      } else {
        showToast(
          response.data.message ||
          (checkInData.id ? "Failed to update check-in point." : "Failed to create check-in point."),
          "error",
        )
      }
    } catch (error) {
      console.error("Error saving check-in point:", error)
      showToast(
        checkInData.id
          ? "Failed to update check-in point. Please try again."
          : "Failed to create check-in point. Please try again.",
        "error",
      )
    }
  }

  const selectedTicketCount = ticketList.filter((t) => t.selected).length

  if (!show && !isVisible) return null

  return (
    <>
      <div
        className={`extraspace_search_div2 add_attendites_model search_div open ${show ? "show" : ""}`}
        style={{

          padding: "0px",

        }}
      >
        <div
          className="modal-dialog modal-xl modal-dialog-centered"
          style={{
            margin: "0px  ",

          }}
        >
          <div
            className="modal-content"
            style={{
              border: "none",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              overflow: "hidden",
              animation: show ? "slideIn 0.3s ease-out" : "slideOut 0.3s ease-in",
            }}
          >
            {/* Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #184778 0%, #2563a8 100%)",
                padding: "24px 32px",
                color: "#ffffff",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "200px",
                  height: "200px",
                  background: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "50%",
                  transform: "translate(50%, -50%)",
                }}
              />
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ position: "relative", zIndex: 1 }}
              >
                <div>
                  <h4
                    style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      margin: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaPlus size={18} />
                    </div>
                    {checkInData.id ? "Edit Check-in Point" : "Create Check-in Point"}
                  </h4>
                  <p style={{ margin: "8px 0 0 52px", fontSize: "14px", opacity: 0.95, fontWeight: "400" }}>
                    {checkInData.id
                      ? "Update your check-in point details"
                      : "Create a new check-in point for your event"}
                  </p>
                </div>
                <button
                  onClick={onHide}
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "10px",
                    color: "#ffffff",
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "18px",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.25)"
                    e.currentTarget.style.transform = "scale(1.05)"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)"
                    e.currentTarget.style.transform = "scale(1)"
                  }}
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div
              style={{
                padding: "32px",
                maxHeight: "calc(100vh - 200px)",
                overflowY: "auto",
                background: "#f8fafc",
              }}
            >
              <div>
                {/* Check-in Name */}
                <div
                  style={{
                    marginBottom: "28px",
                    background: "#ffffff",
                    padding: "20px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <label
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#334155",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Check-in Point Name <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Main Entrance Check-in"
                    className="form-control"
                    style={{
                      padding: "12px 16px",
                      border: `2px solid ${errors.checkin_name ? "#dc2626" : "#e2e8f0"}`,
                      borderRadius: "8px",
                      fontSize: "14px",
                      transition: "all 0.2s ease",
                      fontWeight: "500",
                    }}
                    name="checkin_name"
                    value={checkInData.checkin_name}
                    onChange={handleInputChange}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#184778"
                      e.target.style.boxShadow = "0 0 0 3px rgba(24, 71, 120, 0.1)"
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.checkin_name ? "#dc2626" : "#e2e8f0"
                      e.target.style.boxShadow = "none"
                    }}
                    required
                  />
                  {errors.checkin_name && (
                    <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "4px", margin: 0 }}>
                      {errors.checkin_name}
                    </p>
                  )}
                </div>

                {/* Ticket Selection */}
                <div
                  style={{
                    marginBottom: "28px",
                    background: "#ffffff",
                    padding: "20px",
                    borderRadius: "12px",
                    border: `1px solid ${errors.ticket_ids ? "#fecaca" : "#e2e8f0"}`,
                  }}
                >
                  <h5
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#184778",
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaTicketAlt />
                    Allow Entry with Selected Tickets
                  </h5>
                  <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "12px" }}>
                    Choose which ticket types are valid for this check-in point
                  </p>
                  <button
                    onClick={() => setShowTicketModal(true)}
                    style={{
                      background: errors.ticket_ids ? "#fef2f2" : "#f1f5f9",
                      border: `2px dashed ${errors.ticket_ids ? "#fecaca" : "#cbd5e1"}`,
                      borderRadius: "8px",
                      padding: "12px 20px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#475569",
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      if (!errors.ticket_ids) {
                        e.currentTarget.style.background = "#e2e8f0"
                        e.currentTarget.style.borderColor = "#94a3b8"
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = errors.ticket_ids ? "#fef2f2" : "#f1f5f9"
                      e.currentTarget.style.borderColor = errors.ticket_ids ? "#fecaca" : "#cbd5e1"
                    }}
                  >
                    <FaPlus />
                    Choose Tickets
                    {selectedTicketCount > 0 && (
                      <span
                        style={{
                          marginLeft: "8px",
                          background: "#184778",
                          color: "#ffffff",
                          borderRadius: "12px",
                          padding: "2px 8px",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {selectedTicketCount} selected
                      </span>
                    )}
                  </button>
                  {errors.ticket_ids && (
                    <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "4px", margin: 0 }}>
                      {errors.ticket_ids}
                    </p>
                  )}
                </div>

                {/* Date/Time Selection */}
                <div
                  style={{
                    marginBottom: "28px",
                    background: "#ffffff",
                    padding: "20px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <h5
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#184778",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaCalendarAlt />
                    Check-in Period
                  </h5>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#334155",
                          marginBottom: "8px",
                          display: "block",
                        }}
                      >
                        Start Date & Time <span style={{ color: "#dc2626" }}>*</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          type="datetime-local"
                          className="form-control"
                          style={{
                            padding: "12px 16px",

                            border: `2px solid ${errors.checkin_start_date ? "#dc2626" : "#e2e8f0"}`,
                            borderRadius: "8px",
                            fontSize: "14px",
                            transition: "all 0.2s ease",
                            fontWeight: "500",
                          }}
                          name="checkin_start_date"
                          value={checkInData.checkin_start_date}
                          onChange={handleInputChange}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#184778"
                            e.target.style.boxShadow = "0 0 0 3px rgba(24, 71, 120, 0.1)"
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = errors.checkin_start_date ? "#dc2626" : "#e2e8f0"
                            e.target.style.boxShadow = "none"
                          }}
                        />
                        {/* <div
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#94a3b8",
                            pointerEvents: "none",
                          }}
                        >
                          <FaCalendarAlt />
                        </div> */}
                      </div>
                      {errors.checkin_start_date && (
                        <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "4px", margin: 0 }}>
                          {errors.checkin_start_date}
                        </p>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#334155",
                          marginBottom: "8px",
                          display: "block",
                        }}
                      >
                        End Date & Time <span style={{ color: "#dc2626" }}>*</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          type="datetime-local"
                          className="form-control"
                          style={{
                            padding: "12px 16px",

                            border: `2px solid ${errors.checkin_end_date ? "#dc2626" : "#e2e8f0"}`,
                            borderRadius: "8px",
                            fontSize: "14px",
                            transition: "all 0.2s ease",
                            fontWeight: "500",
                          }}
                          name="checkin_end_date"
                          value={checkInData.checkin_end_date}
                          onChange={handleInputChange}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#184778"
                            e.target.style.boxShadow = "0 0 0 3px rgba(24, 71, 120, 0.1)"
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = errors.checkin_end_date ? "#dc2626" : "#e2e8f0"
                            e.target.style.boxShadow = "none"
                          }}
                        />
                        {/* <div
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#94a3b8",
                            pointerEvents: "none",
                          }}
                        >
                          <FaCalendarAlt />
                        </div> */}
                      </div>
                      {errors.checkin_end_date && (
                        <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "4px", margin: 0 }}>
                          {errors.checkin_end_date}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Entry Limits */}
                <div
                  style={{
                    marginBottom: "28px",
                    background: "#ffffff",
                    padding: "20px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <h5
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#184778",
                      marginBottom: "12px",
                    }}
                  >
                    Entry Limits
                  </h5>
                  <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
                    Control how many times attendees can check in at this point
                  </p>
                  <div className="d-flex gap-3" style={{ marginBottom: "16px" }}>
                    <button
                      onClick={() => handleEntryTypeChange("single")}
                      style={{
                        background: checkInData.entry_type === "single" ? "#184778" : "#ffffff",
                        color: checkInData.entry_type === "single" ? "#ffffff" : "#475569",
                        border: `2px solid ${checkInData.entry_type === "single" ? "#184778" : "#e2e8f0"}`,
                        borderRadius: "8px",
                        padding: "12px 24px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        flex: 1,
                        transition: "all 0.2s ease",
                        boxShadow: checkInData.entry_type === "single" ? "0 4px 12px rgba(24, 71, 120, 0.2)" : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (checkInData.entry_type !== "single") {
                          e.currentTarget.style.borderColor = "#cbd5e1"
                          e.currentTarget.style.background = "#f8fafc"
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (checkInData.entry_type !== "single") {
                          e.currentTarget.style.borderColor = "#e2e8f0"
                          e.currentTarget.style.background = "#ffffff"
                        }
                      }}
                    >
                      Single Entry
                    </button>
                    <button
                      onClick={() => handleEntryTypeChange("multiple")}
                      style={{
                        background: checkInData.entry_type === "multiple" ? "#184778" : "#ffffff",
                        color: checkInData.entry_type === "multiple" ? "#ffffff" : "#475569",
                        border: `2px solid ${checkInData.entry_type === "multiple" ? "#184778" : "#e2e8f0"}`,
                        borderRadius: "8px",
                        padding: "12px 24px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        flex: 1,
                        transition: "all 0.2s ease",
                        boxShadow: checkInData.entry_type === "multiple" ? "0 4px 12px rgba(24, 71, 120, 0.2)" : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (checkInData.entry_type !== "multiple") {
                          e.currentTarget.style.borderColor = "#cbd5e1"
                          e.currentTarget.style.background = "#f8fafc"
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (checkInData.entry_type !== "multiple") {
                          e.currentTarget.style.borderColor = "#e2e8f0"
                          e.currentTarget.style.background = "#ffffff"
                        }
                      }}
                    >
                      Multiple Entry
                    </button>
                  </div>
                  {checkInData.entry_type === "multiple" && (
                    <div
                      style={{
                        marginTop: "16px",
                        padding: "16px",
                        background: "#f8fafc",
                        borderRadius: "8px",
                        border: `1px solid ${errors.entry_limit ? "#fecaca" : "#e2e8f0"}`,
                      }}
                    >
                      <label
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#334155",
                          marginBottom: "8px",
                          display: "block",
                        }}
                      >
                        Maximum Entry Limit <span style={{ color: "#dc2626" }}>*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        className="form-control"
                        style={{
                          padding: "12px 16px",
                          border: `2px solid ${errors.entry_limit ? "#dc2626" : "#e2e8f0"}`,
                          borderRadius: "8px",
                          fontSize: "14px",
                          width: "150px",
                          fontWeight: "500",
                          transition: "all 0.2s ease",
                        }}
                        name="entry_limit"
                        value={checkInData.entry_limit}
                        onChange={handleInputChange}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#184778"
                          e.target.style.boxShadow = "0 0 0 3px rgba(24, 71, 120, 0.1)"
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = errors.entry_limit ? "#dc2626" : "#e2e8f0"
                          e.target.style.boxShadow = "none"
                        }}
                        required
                      />
                      {errors.entry_limit && (
                        <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "4px", margin: 0 }}>
                          {errors.entry_limit}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Messages */}
                <div
                  style={{
                    background: "#ffffff",
                    padding: "20px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <h5
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#184778",
                      marginBottom: "16px",
                    }}
                  >
                    Custom Messages
                  </h5>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#334155",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          width: "20px",
                          height: "20px",
                          background: "#10b981",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#ffffff",
                          fontSize: "12px",
                        }}
                      >
                        <FaCheck />
                      </span>
                      Successful check-in message
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Welcome! Check-in successful."
                      className="form-control"
                      style={{
                        padding: "12px 16px",
                        border: `2px solid ${errors.checkin_success_msg ? "#dc2626" : "#e2e8f0"}`,
                        borderRadius: "8px",
                        fontSize: "14px",
                        transition: "all 0.2s ease",
                      }}
                      name="checkin_success_msg"
                      value={checkInData.checkin_success_msg}
                      onChange={handleInputChange}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#10b981"
                        e.target.style.boxShadow = "0 0 0 3px rgba(16, 185, 129, 0.1)"
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e2e8f0"
                        e.target.style.boxShadow = "none"
                      }}
                    />
                    {errors.checkin_success_msg && (
                      <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "4px", margin: 0 }}>
                        {errors.checkin_success_msg}
                      </p>
                    )}
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#334155",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          width: "20px",
                          height: "20px",
                          background: "#dc2626",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#ffffff",
                          fontSize: "12px",
                        }}
                      >
                        <FaTimes />
                      </span>
                      Failed check-in message
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Invalid ticket. Please contact support."
                      className="form-control"
                      style={{
                        padding: "12px 16px",
                        border: `2px solid ${errors.checkin_fail_msg ? "#dc2626" : "#e2e8f0"}`,
                        borderRadius: "8px",
                        fontSize: "14px",
                        transition: "all 0.2s ease",
                      }}
                      name="checkin_fail_msg"
                      value={checkInData.checkin_fail_msg}
                      onChange={handleInputChange}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#dc2626"
                        e.target.style.boxShadow = "0 0 0 3px rgba(220, 38, 38, 0.1)"
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e2e8f0"
                        e.target.style.boxShadow = "none"
                      }}
                    />
                    {errors.checkin_fail_msg && (
                      <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "4px", margin: 0 }}>
                        {errors.checkin_fail_msg}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#334155",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          width: "20px",
                          height: "20px",
                          background: "#f59e0b",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#ffffff",
                          fontSize: "12px",
                        }}
                      >
                        !
                      </span>
                      Check-in limit exceeded message
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Maximum check-in limit reached."
                      className="form-control"
                      style={{
                        padding: "12px 16px",
                        border: `2px solid ${errors.limit_exceeded_msg ? "#dc2626" : "#e2e8f0"}`,
                        borderRadius: "8px",
                        fontSize: "14px",
                        transition: "all 0.2s ease",
                      }}
                      name="limit_exceeded_msg"
                      value={checkInData.limit_exceeded_msg}
                      onChange={handleInputChange}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#f59e0b"
                        e.target.style.boxShadow = "0 0 0 3px rgba(245, 158, 11, 0.1)"
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e2e8f0"
                        e.target.style.boxShadow = "none"
                      }}
                    />
                    {errors.limit_exceeded_msg && (
                      <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "4px", margin: 0 }}>
                        {errors.limit_exceeded_msg}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                background: "#ffffff",
                padding: "20px 32px",
                borderTop: "2px solid #f1f5f9",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <button
                onClick={onHide}
              className="btn  btn-outline-secondary "
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="btn btn-outline-primary " >
                <FaCheck />
                {checkInData.id ? "Update Check-in Point" : "Create Check-in Point"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Ticket Selection Modal */}
      <Modal show={showTicketModal} onHide={() => setShowTicketModal(false)} centered>
        <div style={{ borderRadius: "12px", overflow: "hidden" }}>
          <Modal.Header
            style={{
              background: "linear-gradient(135deg, #184778 0%, #2563a8 100%)",
              color: "#ffffff",
              border: "none",
              padding: "20px 24px",
            }}
          >
            <Modal.Title
              style={{
                fontSize: "18px",
                fontWeight: "700",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <FaTicketAlt />
              Select Tickets
            </Modal.Title>
            <button
              onClick={() => setShowTicketModal(false)}
              style={{
                background: "rgba(255, 255, 255, 0.15)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "6px",
                color: "#ffffff",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "16px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.25)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)"
              }}
            >
              <FaTimes />
            </button>
          </Modal.Header>
          <Modal.Body
            style={{
              padding: "24px",
              maxHeight: "60vh",
              overflowY: "auto",
              background: "#f8fafc",
            }}
          >
            <div
              style={{
                marginBottom: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>
                {selectedTicketCount} ticket{selectedTicketCount !== 1 ? "s" : ""} selected
              </p>
              <button
                onClick={handleSelectAllTickets}
                style={{
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#475569",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f1f5f9"
                  e.currentTarget.style.borderColor = "#cbd5e1"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#ffffff"
                  e.currentTarget.style.borderColor = "#e2e8f0"
                }}
              >
                {ticketList.every((ticket) => ticket.selected) ? "Deselect All" : "Select All"}
              </button>
            </div>
            {isLoadingTickets ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #e2e8f0",
                    borderTop: "4px solid #184778",
                    borderRadius: "50%",
                    margin: "0 auto 12px",
                    animation: "spin 1s linear infinite",
                  }}
                />
                <p style={{ color: "#64748b", fontSize: "14px" }}>Loading tickets...</p>
              </div>
            ) : ticketError ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  background: "#fef2f2",
                  borderRadius: "8px",
                  border: "1px solid #fecaca",
                }}
              >
                <p style={{ color: "#dc2626", fontSize: "14px", margin: 0 }}>{ticketError}</p>
              </div>
            ) : ticketList.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  background: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <FaTicketAlt size={32} style={{ color: "#cbd5e1", marginBottom: "12px" }} />
                <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>No tickets available</p>
              </div>
            ) : (
              <div style={{ display: "grid", gap: "8px" }}>
                {ticketList.map((ticket) => (
                  <label
                    key={ticket.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 16px",
                      background: "#ffffff",
                      border: `2px solid ${ticket.selected ? "#184778" : "#e2e8f0"}`,
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      fontWeight: ticket.selected ? "600" : "500",
                      color: ticket.selected ? "#184778" : "#334155",
                    }}
                    onMouseEnter={(e) => {
                      if (!ticket.selected) {
                        e.currentTarget.style.borderColor = "#cbd5e1"
                        e.currentTarget.style.background = "#f8fafc"
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!ticket.selected) {
                        e.currentTarget.style.borderColor = "#e2e8f0"
                        e.currentTarget.style.background = "#ffffff"
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={ticket.selected}
                      onChange={() => handleTicketSelect(ticket.id)}
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                        accentColor: "#184778",
                      }}
                    />
                    <FaTicketAlt size={14} style={{ color: ticket.selected ? "#184778" : "#94a3b8" }} />
                    <span style={{ fontSize: "14px", flex: 1 }}>{ticket.ticket_name}</span>
                    {ticket.selected && (
                      <span
                        style={{
                          width: "20px",
                          height: "20px",
                          background: "#184778",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#ffffff",
                          fontSize: "12px",
                        }}
                      >
                        <FaCheck />
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer
            style={{
              background: "#ffffff",
              borderTop: "2px solid #f1f5f9",
              padding: "16px 24px",
            }}
          >
            <button
              onClick={() => setShowTicketModal(false)}
              style={{
                background: "linear-gradient(135deg, #184778 0%, #2563a8 100%)",
                border: "none",
                borderRadius: "8px",
                color: "#ffffff",
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(24, 71, 120, 0.3)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)"
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(24, 71, 120, 0.4)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)"
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(24, 71, 120, 0.3)"
              }}
            >
              Done
            </button>
          </Modal.Footer>
        </div>
      </Modal>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideOut {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(-20px);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  )
}

export default CreateCheckin